/** Fired after work hours are persisted to the DB (e.g. FileModal auto-save). Salary UI can refetch aggregated report. */
export const TIMESHEET_WORKHOURS_PERSISTED = 'timesheet-workhours-persisted';

const BROADCAST_CHANNEL_NAME = 'timesheet-salary-sync';

const listeners = new Set();
const crossTabListeners = new Set();

// ---------------------------------------------------------------------------
// Listener runner functions (defined early so they can be called from event handlers)
// ---------------------------------------------------------------------------

function runListeners() {
    listeners.forEach((fn) => {
        try {
            fn();
        } catch {
            /* ignore subscriber errors */
        }
    });
}

function runCrossTabListeners(payload) {
    crossTabListeners.forEach((fn) => {
        try {
            fn(payload);
        } catch {
            /* ignore subscriber errors */
        }
    });
}

// ---------------------------------------------------------------------------
// Cross-tab communication via BroadcastChannel (with localStorage fallback).
// When a save happens in Tab A, Tab B needs to know so it can refetch.
// ---------------------------------------------------------------------------

function handleIncomingCrossTab(payload) {
    runListeners();
    runCrossTabListeners(payload);
}

let _bc = null;
try {
    if (typeof BroadcastChannel !== 'undefined') {
        _bc = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        _bc.onmessage = (evt) => {
            if (evt?.data?.type === TIMESHEET_WORKHOURS_PERSISTED) {
                handleIncomingCrossTab(evt.data.payload);
            }
        };
    }
} catch {
    // BroadcastChannel not supported — fall through to localStorage fallback
}

// localStorage fallback: other tabs receive a "storage" event when we write.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (evt) => {
        if (evt.key === BROADCAST_CHANNEL_NAME && evt.newValue) {
            try {
                const parsed = JSON.parse(evt.newValue);
                if (parsed?.type === TIMESHEET_WORKHOURS_PERSISTED) {
                    handleIncomingCrossTab(parsed.payload);
                }
            } catch { /* ignore parse errors */ }
        }
    });
}

function broadcastToOtherTabs(payload) {
    // BroadcastChannel (same-origin, reliable, no serialization quirks)
    try {
        if (_bc) {
            _bc.postMessage({ type: TIMESHEET_WORKHOURS_PERSISTED, payload });
        }
    } catch { /* ignore */ }

    // localStorage fallback — the writing tab does NOT receive the "storage" event,
    // only other tabs do, so this is safe from self-triggering loops.
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(
                BROADCAST_CHANNEL_NAME,
                JSON.stringify({ type: TIMESHEET_WORKHOURS_PERSISTED, payload, _t: Date.now() })
            );
        }
    } catch { /* quota / private-mode errors */ }
}

// ---------------------------------------------------------------------------

/**
 * Register a callback invoked whenever timesheet work hours are persisted
 * (same tab via direct call, other tabs via BroadcastChannel / localStorage).
 * @param {() => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeTimesheetWorkHoursPersisted(fn) {
    if (typeof fn !== 'function') return () => {};
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
}

/**
 * Subscribe to cross-tab-only timesheet updates (NOT triggered by saves in the current tab).
 * The callback receives the payload: { timesheetId?, month?, year? }.
 * @param {(payload: object) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeCrossTabTimesheetUpdate(fn) {
    if (typeof fn !== 'function') return () => {};
    crossTabListeners.add(fn);
    return () => {
        crossTabListeners.delete(fn);
    };
}

/**
 * @param {{ timesheetId?: string, month?: number, year?: number }} detail
 */
export function notifyTimesheetWorkHoursPersisted(detail) {
    if (!detail) return;
    const month = Number(detail.month);
    const year = Number(detail.year);

    const payload = { ...detail, month, year };

    // Notify same-tab listeners
    if (typeof window !== 'undefined') {
        window.dispatchEvent(
            new CustomEvent(TIMESHEET_WORKHOURS_PERSISTED, { detail: payload })
        );
    }
    runListeners();

    // Notify OTHER tabs
    broadcastToOtherTabs(payload);
}
